import { faMinus, faPlus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import { ActionButton } from 'components';
import React, { FC, Fragment, useCallback, useEffect, useRef, useState } from 'react';
import _ from "lodash"
import './quantity-adjuster.scss';

type Props = {
    id: number | string
    quantity: number,
    header?: string,
    minValue?: number,
    maxValue?: number,
    iconSize?: FontAwesomeIconProps["size"],
    buttonSize?: string,
    inputDelay?: number,
    dataTestIdForButtons?: string,
    showPlusAndMinusButtons?: boolean,
    onQuantityAdjusted: (id: number | string, quantity: number) => void,
}

export const QuantityAdjuster: FC<Props> = ({ id, quantity, header, minValue = 0, iconSize = '3x', buttonSize = '64px', maxValue = 9999, inputDelay = 0, dataTestIdForButtons, showPlusAndMinusButtons = true, onQuantityAdjusted }) => {

    const hasPendingInput = useRef<boolean>(false)
    const previousQuantity = useRef<number>(quantity)
    const _inputQuantity = useRef<number>(quantity)
    const [inputQuantity, setInputQuantity] = useState<number>(quantity ?? '')

    useEffect(() => {
        //if parent component changes quantity, adjust internal state to match
        if (quantity != _inputQuantity.current || quantity != inputQuantity) {
            hasPendingInput.current = false;
            handleQuantityAdjusted(quantity)
        }
    }, [quantity])

    const updateInputQuantity = (newQuantity: number) => {
        _inputQuantity.current = newQuantity
        setInputQuantity(newQuantity)
    }

    const changeQuantityAfterDelay = useCallback(_.debounce(() => {
        if (hasPendingInput.current) {
            handleQuantityAdjusted()
            hasPendingInput.current = false
        }
    }, inputDelay), []);

    const handleInputValueChanged = (e: any): void => {
        let newValue = +(e.target.value)
        hasPendingInput.current = true
        updateInputQuantity(newValue)
        changeQuantityAfterDelay()
    }

    const handleQuantityIncremented = (): void => {
        const newValue = inputQuantity + 1
        updateInputQuantity(newValue)
        if (hasPendingInput.current == false) {
            handleQuantityAdjusted(newValue)
        }
    }

    const handleQuantityDecremented = (): void => {
        const newValue = inputQuantity > 0 ? inputQuantity - 1 : 0
        updateInputQuantity(newValue)
        if (hasPendingInput.current == false) {
            handleQuantityAdjusted(newValue)
        }
    }

    const handleQuantityAdjusted = (value?: number) => {
        let newValue: number = value ?? _inputQuantity.current

        //set back to old value in the case of invalid input
        if (isNaN(newValue) || newValue < minValue || newValue > maxValue) newValue = previousQuantity.current;

        const hasValueChanged: boolean = newValue !== previousQuantity.current
        previousQuantity.current = newValue
        updateInputQuantity(newValue)

        if (hasValueChanged) {
            onQuantityAdjusted(id, newValue)
        }
    }

    return (
        <Fragment>
            {header &&
                <div className={'qty-adjust-label'}>{header}</div>
            }
            <div className={'qty-adjust-adjuster'}>
                {showPlusAndMinusButtons &&
                    <ActionButton
                        dataTestId={dataTestIdForButtons ? `${dataTestIdForButtons}-minus` : undefined}
                        enabled={quantity > minValue}
                        icon={faMinus}
                        iconSize={iconSize}
                        buttonColor={'#EB5757'}
                        buttonWidth={buttonSize}
                        circular={true}
                        onClick={handleQuantityDecremented}
                    />
                }
                <div className={'qty-adjust-box'}>
                    <input
                        className={'qty-adjust-input'}
                        value={inputQuantity}
                        min={minValue}
                        max={maxValue}
                        type={'text'}
                        pattern={"/^[0-9\b]+$/"}
                        inputMode={"numeric"}
                        onChange={handleInputValueChanged}
                    />
                </div>
                {showPlusAndMinusButtons &&
                    <ActionButton
                        dataTestId={dataTestIdForButtons ? `${dataTestIdForButtons}-plus` : undefined}
                        enabled={quantity < maxValue}
                        icon={faPlus}
                        iconSize={iconSize}
                        buttonColor={'#76AB21'}
                        buttonWidth={buttonSize}
                        circular={true}
                        onClick={handleQuantityIncremented}
                    />
                }
            </div >
        </Fragment>
    )
}